import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Volume2, VolumeX } from 'lucide-react';
import { GerryBubble } from './gerry/GerryBubble';
import { GerryMessageList } from './gerry/GerryMessageList';
import { GerryInputBar } from './gerry/GerryInputBar';
import { GerryQuizCard } from './gerry/GerryQuizCard';
import { useProgressContext } from '../context/ProgressContext';
import {
  GERRY_AVATAR, WEB3_KNOWLEDGE, GAME_HINTS, HERO_STORIES,
  STUCK_TIPS, QUICK_ACTIONS, QUIZ_QUESTIONS
} from './gerry/gerryConstants';

const GERRY_KEY = 'blockquest_gerry';
const GERRY_SETTINGS_KEY = 'blockquest_gerry_settings';
const DEVICE_KEY = 'blockquest_device_id';
const API_URL = process.env.REACT_APP_BACKEND_URL;

const loadHistory = () => {
  try {
    return JSON.parse(localStorage.getItem(GERRY_KEY)) || [];
  } catch (e) { console.warn('Failed to load chat history:', e); return []; }
};

const saveHistory = (h) => {
  try {
    const trimmed = h.slice(-50);
    localStorage.setItem(GERRY_KEY, JSON.stringify(trimmed));
  } catch (e) { console.warn('Failed to save Gerry history:', e); }
};

const loadSettings = () => {
  try {
    return JSON.parse(localStorage.getItem(GERRY_SETTINGS_KEY)) || { enabled: true, voice: true, failCount: 0 };
  } catch (e) { console.warn('Failed to load Gerry settings:', e); return { enabled: true, voice: true, failCount: 0 }; }
};

const saveSettings = (s) => {
  try { localStorage.setItem(GERRY_SETTINGS_KEY, JSON.stringify(s)); } catch (e) { console.warn('Failed to save Gerry settings:', e); }
};

const getResponse = (input, hero) => {
  const q = input.toLowerCase().trim();

  for (const [key, answer] of Object.entries(WEB3_KNOWLEDGE)) {
    if (q.includes(key.replace('_', ' ')) || q.includes(key)) {
      return answer;
    }
  }

  for (const [game, hints] of Object.entries(GAME_HINTS)) {
    if (q.includes(game) || q.includes(game.replace('_', ' '))) {
      return hints[Math.floor(Math.random() * hints.length)];
    }
  }

  if (q.includes('story') || q.includes('what if') || q.includes('imagine')) {
    const heroData = HERO_STORIES[hero] || HERO_STORIES.gerry;
    return heroData.stories[Math.floor(Math.random() * heroData.stories.length)];
  }

  if (q.includes('help') || q.includes('hint') || q.includes('stuck') || q.includes('tip')) {
    return STUCK_TIPS[Math.floor(Math.random() * STUCK_TIPS.length)];
  }

  if (q.includes('hello') || q.includes('hi') || q.includes('hey') || q.includes('sup')) {
    return `Hey there, explorer! I'm Gerry the Goat \u2014 your Web3 buddy! Ask me about blockchain, tokens, NFTs, or say "tell me a story" for a fun tale about ${(HERO_STORIES[hero] || HERO_STORIES.gerry).name}!`;
  }

  if (q.includes('xp') || q.includes('progress') || q.includes('score') || q.includes('level')) {
    return "Check your Quest Log above to see your XP, badges, and hero unlocks! Keep playing games and claiming rewards to level up!";
  }

  const fallbacks = [
    `Great question! Try asking me about specific Web3 topics like "blockchain", "mining", "NFTs", or "wallets". Or say "tell me a story" for a ${(HERO_STORIES[hero] || HERO_STORIES.gerry).name} adventure!`,
    `Hmm, I'm still learning about that one! But I know TONS about Web3 \u2014 try asking about crypto, smart contracts, or tokens!`,
    `Baaaa! That's a thinker. Ask me about blockchain basics, game hints, or say "what if" for a fun hero story!`,
  ];
  return fallbacks[Math.floor(Math.random() * fallbacks.length)];
};

const GerryCompanion = ({ selectedHero = 'gerry', enabled: parentEnabled }) => {
  const { addXp, lastClaimed, getDeviceToken } = useProgressContext();

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(loadHistory);
  const [input, setInput] = useState('');
  const [settings, setSettings] = useState(loadSettings);
  const [showStuckTip, setShowStuckTip] = useState(false);
  const [position, setPosition] = useState({ x: null, y: null });
  const [dragging, setDragging] = useState(false);
  const [expandedImages, setExpandedImages] = useState(new Set());
  const [quizState, setQuizState] = useState(null);
  const dragOffset = useRef({ x: 0, y: 0 });
  const chatEndRef = useRef(null);

  const isEnabled = parentEnabled !== undefined ? parentEnabled : settings.enabled;

  // Scroll chat to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, quizState]);

  // Stuck detection (45s idle resets on message send)
  useEffect(() => {
    if (!isEnabled || !open) return;
    const timer = setTimeout(() => setShowStuckTip(true), 45000);
    return () => clearTimeout(timer);
  }, [messages, open, isEnabled]);

  // Auto-dismiss stuck tip
  useEffect(() => {
    if (!showStuckTip) return;
    const tip = STUCK_TIPS[Math.floor(Math.random() * STUCK_TIPS.length)];
    setMessages(prev => {
      const next = [...prev, { role: 'gerry', text: tip, ts: Date.now() }];
      saveHistory(next);
      return next;
    });
    setShowStuckTip(false);
    if (!open) setOpen(true);
  }, [showStuckTip, open]);

  // Save settings
  useEffect(() => { saveSettings(settings); }, [settings]);

  // Voice synth - young, playful kid-friend voice
  const speak = useCallback((text) => {
    if (!settings.voice || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 1.05;
    u.pitch = 1.5;
    u.volume = 0.9;
    const voices = window.speechSynthesis.getVoices();
    const preferred = [
      'Samantha', 'Karen', 'Fiona', 'Tessa',
      'Google UK English Female', 'Microsoft Zira',
      'Google US English',
    ];
    let picked = null;
    for (const name of preferred) {
      picked = voices.find(v => v.name.includes(name) && v.lang.startsWith('en'));
      if (picked) break;
    }
    if (!picked) picked = voices.find(v => v.lang.startsWith('en') && v.name.toLowerCase().includes('female'));
    if (!picked) picked = voices.find(v => v.lang.startsWith('en'));
    if (picked) u.voice = picked;
    window.speechSynthesis.speak(u);
  }, [settings.voice]);

  const sessionId = useRef('gerry_' + (localStorage.getItem('blockquest_device_id') || 'anon'));
  const syncTimeout = useRef(null);

  // Sync conversation to cloud (debounced)
  const syncToCloud = useCallback(async (msgs) => {
    const deviceId = localStorage.getItem(DEVICE_KEY);
    if (!API_URL || !deviceId) return;
    const token = getDeviceToken ? getDeviceToken() : '';
    try {
      await fetch(`${API_URL}/api/gerry/history/${deviceId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'X-Device-Token': token } : {}) },
        body: JSON.stringify({ messages: msgs.slice(-50), game: 'hub' })
      });
    } catch (e) {
      console.warn('Gerry history sync failed', e);
    }
  }, [getDeviceToken]);

  const debouncedCloudSync = useCallback((msgs) => {
    clearTimeout(syncTimeout.current);
    syncTimeout.current = setTimeout(() => syncToCloud(msgs), 2000);
  }, [syncToCloud]);

  // Fetch and merge cloud history on mount
  useEffect(() => {
    const fetchCloudHistory = async () => {
      const deviceId = localStorage.getItem(DEVICE_KEY);
      if (!API_URL || !deviceId) return;
      const token = getDeviceToken ? getDeviceToken() : '';
      try {
        const resp = await fetch(`${API_URL}/api/gerry/history/${deviceId}`, {
          headers: token ? { 'X-Device-Token': token } : {}
        });
        const data = await resp.json();
        if (data.messages && data.messages.length > 0) {
          setMessages(prev => {
            const localTs = new Set(prev.map(m => m.ts));
            const newFromCloud = data.messages.filter(m => !localTs.has(m.ts));
            if (newFromCloud.length === 0) return prev;
            const merged = [...prev, ...newFromCloud].sort((a, b) => a.ts - b.ts).slice(-50);
            saveHistory(merged);
            return merged;
          });
        }
      } catch (e) {
        console.warn('Could not fetch Gerry cloud history', e);
      }
    };
    fetchCloudHistory();
  }, [getDeviceToken]);

  // ─── Quiz Logic ─────────────────────────────────────────
  const startQuiz = useCallback(() => {
    if (quizState) return; // already in quiz
    const shuffled = [...QUIZ_QUESTIONS].sort(() => Math.random() - 0.5);
    const picked = shuffled.slice(0, 3);
    setQuizState({ questions: picked, currentIndex: 0, score: 0, answered: null });

    const intro = {
      role: 'gerry',
      text: "Quiz time! Let's see what you've learned. I'll ask 3 questions \u2014 you earn 5 XP for each correct answer. Let's go!",
      ts: Date.now()
    };
    setMessages(prev => {
      const next = [...prev, intro];
      saveHistory(next);
      return next;
    });
    speak(intro.text);
    if (!open) setOpen(true);
  }, [quizState, speak, open]);

  const handleQuizAnswer = useCallback((answerIndex) => {
    if (!quizState || quizState.answered !== null) return;

    const question = quizState.questions[quizState.currentIndex];
    const isCorrect = answerIndex === question.correct;
    const newScore = quizState.score + (isCorrect ? 1 : 0);
    const nextIndex = quizState.currentIndex + 1;
    const totalQ = quizState.questions.length;

    setQuizState(prev => prev ? { ...prev, answered: answerIndex, score: newScore } : null);

    if (isCorrect) {
      addXp(5);
      speak("That's right! Plus 5 XP!");
    } else {
      speak(`Not quite! The answer was ${['A', 'B', 'C', 'D'][question.correct]}.`);
    }

    setTimeout(() => {
      if (nextIndex >= totalQ) {
        const totalXp = newScore * 5;
        const summary = {
          role: 'gerry',
          text: `Quiz complete! You got ${newScore}/${totalQ} right and earned ${totalXp} XP! Keep exploring to learn even more!`,
          ts: Date.now()
        };
        setMessages(prev => {
          const next = [...prev, summary];
          saveHistory(next);
          debouncedCloudSync(next);
          return next;
        });
        speak(summary.text);
        setQuizState(null);
      } else {
        setQuizState(prev => prev ? { ...prev, currentIndex: nextIndex, answered: null } : null);
      }
    }, 1800);
  }, [quizState, addXp, speak, debouncedCloudSync]);

  // Auto-prompt quiz after quest claim
  const lastClaimedRef = useRef(null);
  useEffect(() => {
    if (lastClaimed && lastClaimed !== 'daily' && lastClaimed !== lastClaimedRef.current && !quizState) {
      lastClaimedRef.current = lastClaimed;
      const timer = setTimeout(() => startQuiz(), 3000);
      return () => clearTimeout(timer);
    }
  }, [lastClaimed, quizState, startQuiz]);

  // Expose quiz trigger globally for games
  useEffect(() => {
    window.__gerryStartQuiz = startQuiz;
    return () => { delete window.__gerryStartQuiz; };
  }, [startQuiz]);

  // Send message
  const send = useCallback(async () => {
    const text = input.trim();
    if (!text) return;
    setInput('');

    // Detect quiz trigger
    const lower = text.toLowerCase();
    if (lower === 'quiz me' || lower === 'quiz me!' || lower === 'quiz') {
      const userMsg = { role: 'user', text, ts: Date.now() };
      setMessages(prev => {
        const next = [...prev, userMsg];
        saveHistory(next);
        return next;
      });
      startQuiz();
      return;
    }

    const userMsg = { role: 'user', text, ts: Date.now() };
    setMessages(prev => {
      const next = [...prev, userMsg];
      saveHistory(next);
      return next;
    });

    let reply;
    const deviceId = localStorage.getItem(DEVICE_KEY);
    try {
      const resp = await fetch(`${API_URL}/api/gerry/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, hero: selectedHero, session_id: sessionId.current, device_id: deviceId })
      });
      const data = await resp.json();
      reply = data.reply;
    } catch (e) {
      console.warn('LLM chat failed, using local response:', e);
      reply = getResponse(text, selectedHero);
    }

    const gerryMsg = { role: 'gerry', text: reply, ts: Date.now() + 1 };
    setMessages(prev => {
      const next = [...prev, gerryMsg];
      saveHistory(next);
      debouncedCloudSync(next);
      return next;
    });
    speak(reply);
  }, [input, selectedHero, speak, debouncedCloudSync, startQuiz]);

  // Difficulty auto-scaling: track fail mentions
  const reportFail = useCallback(() => {
    setSettings(prev => {
      const next = { ...prev, failCount: (prev.failCount || 0) + 1 };
      if (next.failCount >= 3) {
        localStorage.setItem('blockquest_difficulty', 'easy');
        setMessages(p => {
          const msg = { role: 'gerry', text: "Hey, I noticed you're having a tough time! I've told the game to make things a bit easier for you. You've got this! Remember: every expert was once a beginner.", ts: Date.now() };
          const n = [...p, msg];
          saveHistory(n);
          return n;
        });
        if (!open) setOpen(true);
      }
      return next;
    });
  }, [open]);

  // Expose reportFail globally for games
  useEffect(() => {
    window.__gerryReportFail = reportFail;
    return () => { delete window.__gerryReportFail; };
  }, [reportFail]);

  // Dragging
  const onDragStart = (e) => {
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const bubble = e.currentTarget.closest('[data-gerry-root]');
    const rect = bubble.getBoundingClientRect();
    dragOffset.current = { x: clientX - rect.left, y: clientY - rect.top };
    setDragging(true);
  };
  const onDragMove = useCallback((e) => {
    if (!dragging) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    setPosition({
      x: Math.max(0, Math.min(window.innerWidth - 70, clientX - dragOffset.current.x)),
      y: Math.max(0, Math.min(window.innerHeight - 70, clientY - dragOffset.current.y)),
    });
  }, [dragging]);
  const onDragEnd = useCallback(() => setDragging(false), []);

  useEffect(() => {
    if (!dragging) return;
    window.addEventListener('mousemove', onDragMove);
    window.addEventListener('mouseup', onDragEnd);
    window.addEventListener('touchmove', onDragMove, { passive: false });
    window.addEventListener('touchend', onDragEnd);
    return () => {
      window.removeEventListener('mousemove', onDragMove);
      window.removeEventListener('mouseup', onDragEnd);
      window.removeEventListener('touchmove', onDragMove);
      window.removeEventListener('touchend', onDragEnd);
    };
  }, [dragging, onDragMove, onDragEnd]);

  // Welcome message on first open - personalized for returning players
  const greetingFetched = useRef(false);
  const onToggle = async () => {
    if (!open && messages.length === 0 && !greetingFetched.current) {
      greetingFetched.current = true;
      const deviceId = localStorage.getItem(DEVICE_KEY);
      let welcomeText;

      if (API_URL && deviceId) {
        try {
          const token = getDeviceToken ? getDeviceToken() : '';
          const resp = await fetch(`${API_URL}/api/gerry/greeting/${deviceId}`, {
            headers: token ? { 'X-Device-Token': token } : {}
          });
          const data = await resp.json();
          welcomeText = data.greeting;
        } catch (e) { console.warn('Greeting fetch failed, using fallback:', e); }
      }

      if (!welcomeText) {
        const heroData = HERO_STORIES[selectedHero] || HERO_STORIES.gerry;
        welcomeText = `Baaaa! Hey there, I'm Gerry the Goat! Your personal Web3 buddy. Ask me anything about blockchain, crypto, or NFTs \u2014 or say "tell me a story" for a ${heroData.name} adventure! I'm here to help whenever you're stuck.`;
      }

      const welcome = { role: 'gerry', text: welcomeText, ts: Date.now() };
      setMessages([welcome]);
      saveHistory([welcome]);
      speak(welcome.text);
    }
    setOpen(!open);
  };

  if (!isEnabled) return null;

  const bubbleStyle = position.x !== null
    ? { position: 'fixed', left: position.x, top: position.y, zIndex: 9999 }
    : { position: 'fixed', bottom: 24, left: 24, zIndex: 9999 };

  return (
    <div data-gerry-root style={bubbleStyle} data-testid="gerry-companion">
      {open && (
        <div
          className="mb-3 w-80 sm:w-96 rounded-2xl overflow-hidden shadow-2xl border border-orange-500/30"
          style={{
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
            boxShadow: '0 0 40px rgba(255,107,53,0.2), 0 0 80px rgba(155,93,229,0.1)',
            position: position.x !== null ? 'absolute' : 'relative',
            bottom: position.x !== null ? 70 : undefined,
            left: position.x !== null ? 0 : undefined,
          }}
          data-testid="gerry-chat-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-orange-600/90 to-amber-600/90">
            <div className="flex items-center gap-2">
              <img src={GERRY_AVATAR} alt="Gerry" className="w-7 h-7 rounded-full object-cover" />
              <div>
                <p className="text-sm font-black text-white tracking-wide">GERRY <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-400 ml-1 align-middle" title="Synced across games" /></p>
                <p className="text-[10px] text-orange-200 font-medium">Web3 Companion</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSettings(p => ({ ...p, voice: !p.voice }))}
                className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                data-testid="gerry-voice-toggle"
                title={settings.voice ? 'Mute Gerry' : 'Unmute Gerry'}
              >
                {settings.voice ? <Volume2 className="w-4 h-4 text-white" /> : <VolumeX className="w-4 h-4 text-white/50" />}
              </button>
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                data-testid="gerry-close"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <GerryMessageList
            messages={messages}
            expandedImages={expandedImages}
            setExpandedImages={setExpandedImages}
            messagesEndRef={chatEndRef}
          />

          {/* Quiz Card */}
          {quizState && (
            <GerryQuizCard
              question={quizState.questions[quizState.currentIndex]}
              questionNum={quizState.currentIndex + 1}
              totalQuestions={quizState.questions.length}
              answered={quizState.answered}
              onAnswer={handleQuizAnswer}
            />
          )}

          {/* Quick actions + Input */}
          <GerryInputBar
            input={input}
            setInput={setInput}
            onSend={send}
            quickActions={QUICK_ACTIONS}
          />

          {/* Difficulty indicator */}
          {(settings.failCount || 0) >= 3 && (
            <div className="px-3 pb-2">
              <p className="text-[10px] text-green-400 text-center">Easy mode active &mdash; Gerry&apos;s got your back!</p>
            </div>
          )}
        </div>
      )}

      {/* Floating bubble */}
      <GerryBubble
        open={open}
        messages={messages}
        dragging={dragging}
        onToggle={onToggle}
        onHover={() => !open && speak("Need help? Tap me for Web3 tips and game hints!")}
        onDragStart={onDragStart}
      />
    </div>
  );
};

export default GerryCompanion;
